import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/formatting';

// Helper function to get localized text from bilingual object
function getLocalizedText(textObj, lang = 'en') {
    if (typeof textObj === 'string') return textObj;
    if (typeof textObj === 'object' && textObj !== null) {
        return textObj[lang] || textObj.en || '';
    }
    return '';
}

function EventManagement() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language?.startsWith('tl') ? 'tl' : 'en';
    const { isAdmin, canManageEvents, getAuthToken } = useAuth();

    const [event, setEvent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [removeStatus, setRemoveStatus] = useState({});
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [attendeeToRemove, setAttendeeToRemove] = useState(null);

    const fetchEvent = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`/api/events/${id}`);
            if (!response.ok) {
                throw new Error('Failed to fetch event');
            }
            const data = await response.json();
            setEvent(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (!canManageEvents) {
            navigate('/events');
            return;
        }
        fetchEvent();
    }, [id, canManageEvents, navigate, fetchEvent]);

    function handleRemoveClick(attendee) {
        setAttendeeToRemove(attendee);
        setShowRemoveModal(true);
    }

    async function confirmRemoveAttendee() {
        if (!attendeeToRemove) return;

        const attendeeId = attendeeToRemove.userId || attendeeToRemove.phone;

        try {
            setRemoveStatus(prev => ({ ...prev, [attendeeId]: { loading: true } }));
            const token = await getAuthToken();

            const response = await fetch(`/api/events/${id}/signup/${encodeURIComponent(attendeeId)}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to remove attendee');
            }

            setRemoveStatus(prev => ({ ...prev, [attendeeId]: { success: true } }));
            setShowRemoveModal(false);
            setAttendeeToRemove(null);

            // Refresh event data
            fetchEvent();
        } catch (err) {
            setRemoveStatus(prev => ({ ...prev, [attendeeId]: { error: err.message } }));
        }
    }

    function formatSignupDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleDateString(currentLang === 'tl' ? 'fil-PH' : 'en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    }

    function getAttendeeIdentifier(attendee) {
        return attendee.userId || attendee.phone;
    }

    if (!canManageEvents) {
        return (
            <div className="page-section">
                <div className="error-container">
                    <div className="error-icon">🔒</div>
                    <p className="error-message">{t('auth_access_denied')}</p>
                    <Link to="/events" className="btn-primary-modern">{t('admin_back_to_events')}</Link>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">{t('loading')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-section">
                <div className="error-container">
                    <div className="error-icon">⚠️</div>
                    <p className="error-message">{t('error')}: {error}</p>
                    <Link to="/events" className="btn-primary-modern">{t('admin_back_to_events')}</Link>
                </div>
            </div>
        );
    }

    if (!event) {
        return null;
    }

    const attendees = event.attendees || [];
    const maxSlots = event.maxSlots || 5;
    const slotsUsed = attendees.length;

    return (
        <div className="page-section">
            {/* Header */}
            <div className="section-header">
                <div>
                    <Link to="/events" className="back-link">← {t('admin_back_to_events')}</Link>
                    <h2 className="section-title">{t('admin_manage_event')}</h2>
                </div>
            </div>

            {/* Event Details Card */}
            <div className="admin-card">
                <h3 className="admin-card-title">{t('admin_event_details')}</h3>
                <div className="event-details-grid">
                    <div className="event-detail-item">
                        <span className="detail-label">{t('create_event_name')}:</span>
                        <span className="detail-value">{getLocalizedText(event.name, currentLang)}</span>
                    </div>
                    <div className="event-detail-item">
                        <span className="detail-label">{t('when')}</span>
                        <span className="detail-value">{event.start_time ? formatDate(event.start_time, currentLang) : 'TBD'}</span>
                    </div>
                    <div className="event-detail-item">
                        <span className="detail-label">{t('where')}</span>
                        <span className="detail-value">{event.place?.name || event.place || 'TBD'}</span>
                    </div>
                    <div className="event-detail-item">
                        <span className="detail-label">{t('description')}:</span>
                        <span className="detail-value">{getLocalizedText(event.description, currentLang)}</span>
                    </div>
                </div>

                {/* Slots indicator */}
                <div className={`slots-indicator admin-slots ${slotsUsed >= maxSlots ? 'full' : ''}`}>
                    <span className="slots-icon">🎟️</span>
                    <span className="slots-text">
                        {slotsUsed} / {maxSlots} {t('admin_slots_filled')}
                    </span>
                    <div className="slots-bar">
                        <div
                            className="slots-bar-fill"
                            style={{ width: `${(slotsUsed / maxSlots) * 100}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* Attendees Section */}
            <div className="admin-card">
                <h3 className="admin-card-title">👥 {t('admin_attendees')} ({attendees.length})</h3>

                {attendees.length === 0 ? (
                    <div className="empty-state">
                        <p>{t('admin_no_attendees')}</p>
                    </div>
                ) : (
                    <div className="attendees-list">
                        {attendees.map((attendee, index) => {
                            const attendeeId = getAttendeeIdentifier(attendee);
                            const status = removeStatus[attendeeId];

                            return (
                                <div key={attendeeId || index} className="attendee-item">
                                    <div className="attendee-info">
                                        <div className="attendee-primary">
                                            {attendee.isAnonymous ? (
                                                <>
                                                    <span className="attendee-name">{attendee.name}</span>
                                                    <span className="attendee-badge guest">{t('admin_attendee_anonymous')}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="attendee-name">{attendee.email}</span>
                                                    <span className="attendee-badge registered">{t('admin_attendee_authenticated')}</span>
                                                </>
                                            )}
                                        </div>
                                        <div className="attendee-secondary">
                                            {attendee.phone && (
                                                <a
                                                    href={`https://wa.me/${attendee.phone.replace(/\D/g, '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="attendee-phone"
                                                    title={t('phone_whatsapp_title')}
                                                >
                                                    📱 {attendee.phoneFormatted || attendee.phone}
                                                </a>
                                            )}
                                            <span className="attendee-signup-date">
                                                {t('admin_signed_up_at')}: {formatSignupDate(attendee.signedUpAt)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="attendee-actions">
                                        {status?.success ? (
                                            <span className="success-badge">✓</span>
                                        ) : status?.error ? (
                                            <span className="error-badge">{status.error}</span>
                                        ) : (
                                            <button
                                                className="btn-danger-small"
                                                onClick={() => handleRemoveClick(attendee)}
                                                disabled={status?.loading}
                                            >
                                                {status?.loading ? t('admin_removing') : t('admin_remove_attendee')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Remove Confirmation Modal */}
            {showRemoveModal && attendeeToRemove && (
                <div className="modal-overlay" onClick={() => setShowRemoveModal(false)}>
                    <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <h3 className="modal-title">⚠️ {t('admin_remove_confirm_title')}</h3>
                        <p className="modal-message">
                            {t('admin_remove_confirm_message')}
                        </p>
                        <p className="modal-detail">
                            <strong>{attendeeToRemove.name || attendeeToRemove.email}</strong>
                        </p>
                        <div className="modal-actions">
                            <button
                                className="btn-outline-modern"
                                onClick={() => {
                                    setShowRemoveModal(false);
                                    setAttendeeToRemove(null);
                                }}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                className="btn-danger-modern"
                                onClick={confirmRemoveAttendee}
                            >
                                {t('admin_remove_confirm_btn')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EventManagement;
